import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bus, CarTaxiFront, Car, Ship, Hotel, Users, MapPin, Search, Flag, ArrowRight } from "lucide-react";
import { Spinner } from "../components/common";
import { GuideTeaser } from "../components/ArticleSections";
import NewsFeed from "../components/NewsFeed";
import { GUIDES } from "../content/guides";
import { SITE } from "../lib/site";
import api from "../lib/api";
import TripAutoHero from "../components/TripAutoHero";

export const MODE_CONFIG = {
  bus: {
    title: "Bus & Coach", accent: "#7c3aed", icon: Bus, code: "BUS",
    subtitle: "Intercity coaches, bus terminals, cross-border lines, night buses and airport services worldwide.",
    tabs: [["hubs", "Stations & Terminals"], ["operators", "Operators"], ["routes", "Routes"], ["countries", "Country Index"]],
    hubNoun: "terminal",
  },
  ground: {
    title: "Ground Transportation", accent: "#d97706", icon: CarTaxiFront, code: "GROUND",
    subtitle: "Taxis, airport transfers, chauffeurs, car rental, parking and micromobility — door to door, worldwide.",
    tabs: [["operators", "Providers"], ["countries", "Country Index"]],
  },
  rideshare: {
    title: "Ride Share & Shared Mobility", accent: "#0d9488", icon: Users, code: "RIDE",
    subtitle: "Ride-hailing apps, carpooling communities and on-demand microtransit around the world — clearly classified, never confused with taxis.",
    tabs: [["operators", "Providers"], ["countries", "Country Index"]],
  },
  road: {
    title: "Road Travel", accent: "#dc2626", icon: Car, code: "ROAD",
    subtitle: "The world's great scenic drives and highways, plus EV charging networks, RV rentals and roadside assistance.",
    tabs: [["routes", "Scenic Roads & Highways"], ["operators", "Road Services"], ["countries", "Country Index"]],
  },
  water: {
    title: "Water Travel", accent: "#0891b2", icon: Ship, code: "WATER",
    subtitle: "Ferries, harbour crossings, cruise lines, ports and coastal voyages across the world's waters.",
    tabs: [["operators", "Ferry & Cruise Lines"], ["hubs", "Ports & Terminals"], ["routes", "Routes & Crossings"], ["countries", "Country Index"]],
    hubNoun: "port",
  },
  stay: {
    title: "Stay", accent: "#db2777", icon: Hotel, code: "STAY",
    subtitle: "Hotel chains, vacation-rental platforms, hostels and campgrounds — where every journey rests.",
    tabs: [["operators", "Brands & Platforms"], ["countries", "Country Index"]],
    cta: { label: "Hotel deals & travel partners →", to: "/hotels" },
  },
};

const useFetch = (url, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    const t = setTimeout(() => api.get(url, { params }).then((r) => setData(r.data)).catch(() => setData({ items: [] })), 250);
    return () => clearTimeout(t);
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
};

export default function TransportVertical({ mode }) {
  const cfg = MODE_CONFIG[mode];
  const [tab, setTab] = useState(cfg.tabs[0][0]);
  const [q, setQ] = useState("");
  const [overview, setOverview] = useState(null);
  useEffect(() => { api.get(`/transport/${mode}/overview`).then((r) => setOverview(r.data)).catch(() => {}); }, [mode]);

  const stats = overview ? [["Operators", overview.operators], ["Hubs", overview.hubs], ["Routes", overview.routes], ["Countries", overview.countries]].filter(([, v]) => v > 0) : [];

  const heroQueryMap = {
    bus: "intercity bus coach travel",
    ground: "airport taxi city transport",
    rideshare: "rideshare car mobility city",
    road: "scenic road trip highway",
    water: "ferry cruise ship harbor",
    stay: "boutique hotel lobby travel",
  };

  return (
    <div data-testid={`transport-page-${mode}`}>
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query={heroQueryMap[mode] || cfg.title} alt={cfg.title}
          className="absolute inset-0 w-full h-full" testid={`transport-hero-${mode}`} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${cfg.accent}dd 0%, ${cfg.accent}99 55%, rgba(15,23,42,0.55) 100%)` }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <p className="text-xs font-mono uppercase tracking-widest text-white/85">{SITE.name} · {cfg.title}</p>
          <h1 className="mt-2 font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white flex items-center gap-3">
            <cfg.icon className="w-9 h-9 text-white/90" /> {cfg.title}
          </h1>
          <p className="mt-3 text-base text-white/90 max-w-3xl">{cfg.subtitle}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <GuideTeaser guide={GUIDES[mode]} />
        {stats.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid={`${mode}-stats`}>
            {stats.map(([label, value]) => (
              <div key={label} className="border border-zinc-300 bg-white px-5 py-3 min-w-[120px]">
                <div className="font-heading font-900 text-2xl" style={{ color: cfg.accent }}>{value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
              </div>
            ))}
            {cfg.cta && (
              <Link to={cfg.cta.to} data-testid={`${mode}-cta`}
                className="border px-5 py-3 flex items-center font-700 text-sm text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: cfg.accent, borderColor: cfg.accent }}>
                {cfg.cta.label}
              </Link>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {cfg.tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`${mode}-tab-${id}`}
              className={`px-4 py-2 text-sm font-700 border transition-colors ${tab === id ? "text-white" : "bg-white border-zinc-300 text-zinc-700"}`}
              style={tab === id ? { backgroundColor: cfg.accent, borderColor: cfg.accent } : {}}>
              {label}
            </button>
          ))}
        </div>

        {tab !== "countries" && (
          <div className="relative mb-6 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${mode}-search-input`}
              placeholder="Search by name, city or country…"
              className="w-full border border-zinc-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none" />
          </div>
        )}

        {tab === "operators" && <OperatorGrid mode={mode} q={q} accent={cfg.accent} />}
        {tab === "hubs" && <HubGrid mode={mode} q={q} accent={cfg.accent} />}
        {tab === "routes" && <RouteGrid mode={mode} q={q} />}
        {tab === "countries" && <CountryTable mode={mode} />}

        {(mode === "stay" || mode === "water") && (
          <div className="mt-12" data-testid={`${mode}-news-section`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-800 text-lg">
                {mode === "stay" ? "Hotels & Resorts News" : "Cruise News"}
              </h2>
              <Link to="/travel-news" data-testid={`${mode}-news-see-all`}
                className="text-sm text-blue-700 font-600 hover:underline">All travel news →</Link>
            </div>
            <NewsFeed path={`/news?category=${mode === "stay" ? "hotels" : "cruise"}&limit=6`} />
          </div>
        )}
      </div>
    </div>
  );
}

function OperatorGrid({ mode, q, accent }) {
  const data = useFetch(`/transport/${mode}/operators`, useMemo(() => ({ q: q || undefined }), [q]));
  if (!data) return <Spinner />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${mode}-operator-list`}>
      {data.items.map((o) => (
        <Link key={o.id} to={`/t/${mode}/operators/${o.id}`} data-testid={`${mode}-operator-${o.id}`}
          className="border border-zinc-300 bg-white p-5 hover:-translate-y-px transition-[transform,border-color]"
          style={{ "--tw-border-opacity": 1 }} onMouseEnter={(e) => e.currentTarget.style.borderColor = accent} onMouseLeave={(e) => e.currentTarget.style.borderColor = ""}>
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading font-700 text-lg leading-snug">{o.common_name}</h3>
            <span className="text-xs font-mono uppercase border border-zinc-300 px-1.5 py-0.5 text-zinc-600 whitespace-nowrap">{(o.type || "").replaceAll("_", " ")}</span>
          </div>
          <div className="text-sm text-muted-foreground mt-1">{o.country} · {o.service_area}</div>
          <p className="text-sm text-zinc-700 mt-2 line-clamp-2">{o.description}</p>
        </Link>
      ))}
      {data.items.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No matches yet — coverage is expanding continuously.</p>}
    </div>
  );
}

function HubGrid({ mode, q, accent }) {
  const data = useFetch(`/transport/${mode}/hubs`, useMemo(() => ({ q: q || undefined }), [q]));
  if (!data) return <Spinner />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${mode}-hub-list`}>
      {data.items.map((h) => (
        <Link key={h.id} to={`/t/${mode}/hubs/${h.id}`} data-testid={`${mode}-hub-${h.id}`}
          className="border border-zinc-300 bg-white p-5 hover:-translate-y-px transition-[transform,border-color]"
          onMouseEnter={(e) => e.currentTarget.style.borderColor = accent} onMouseLeave={(e) => e.currentTarget.style.borderColor = ""}>
          <h3 className="font-heading font-700 text-lg leading-snug">{h.name}</h3>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{h.city}, {h.country}</div>
          <div className="text-xs text-zinc-600 mt-2 capitalize">{(h.hub_type || "").replaceAll("_", " ")}{h.nearby_airport ? ` · near ${h.nearby_airport}` : ""}</div>
        </Link>
      ))}
    </div>
  );
}

export function TransportRouteCard({ r }) {
  return (
    <div className="border border-zinc-300 bg-white p-5 hover:border-zinc-400 transition-colors" data-testid={`route-${r.id}`}>
      {r.article_slug ? (
        <Link to={`/articles/${r.article_slug}`} data-testid={`route-link-${r.id}`}
          className="font-heading font-700 text-lg leading-snug text-zinc-900 underline decoration-dotted underline-offset-4 hover:decoration-solid hover:text-primary">
          {r.name}
        </Link>
      ) : (
        <h3 className="font-heading font-700 text-lg leading-snug">{r.name}</h3>
      )}
      <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
        <span>{r.origin}</span><ArrowRight className="w-3.5 h-3.5" /><span>{r.destination}</span>
        {r.duration && <span>· {r.duration}</span>}
        {r.distance_km && <span>· {r.distance_km} km</span>}
      </div>
      <p className="text-sm text-zinc-700 mt-2">{r.description}</p>
      <div className="flex items-center justify-between mt-3 text-xs">
        {r.article_slug
          ? <Link to={`/articles/${r.article_slug}`} className="font-700 text-primary hover:underline">Full guide →</Link>
          : <span className="text-muted-foreground">Last verified {r.last_verified}</span>}
        {r.booking_url && <a href={r.booking_url} target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline">Official site ↗</a>}
      </div>
    </div>
  );
}

function RouteGrid({ mode, q }) {
  const data = useFetch(`/transport/${mode}/routes`, useMemo(() => ({ q: q || undefined }), [q]));
  if (!data) return <Spinner />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${mode}-route-list`}>
      {data.items.map((r) => <TransportRouteCard key={r.id} r={r} />)}
    </div>
  );
}

function CountryTable({ mode }) {
  const data = useFetch(`/transport/${mode}/countries`, useMemo(() => ({}), []));
  if (!data) return <Spinner />;
  return (
    <div className="border border-zinc-300 bg-white" data-testid={`${mode}-country-index`}>
      <div className="grid grid-cols-4 px-5 py-3 border-b border-zinc-200 text-xs font-mono uppercase tracking-wide text-muted-foreground">
        <span>Country</span><span className="text-right">Operators</span><span className="text-right">Hubs</span><span className="text-right">Routes</span>
      </div>
      {data.items.map((c) => (
        <div key={c.country_code} className="grid grid-cols-4 px-5 py-2.5 border-b border-zinc-100 text-sm">
          <span className="font-600 flex items-center gap-1.5"><Flag className="w-3.5 h-3.5 text-muted-foreground" />{c.country}</span>
          <span className="text-right tabular-nums">{c.operators}</span>
          <span className="text-right tabular-nums">{c.hubs}</span>
          <span className="text-right tabular-nums">{c.routes}</span>
        </div>
      ))}
    </div>
  );
}
